import React, { useMemo, useRef, useState } from 'react';

import type { EChartsOption } from 'echarts';
import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { Alert, Box, Button, Chip, Container, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import ProductPerformanceComparisons from './ProductPerformanceComparisons';
import ProductPerformanceFilters from './ProductPerformanceFilters';
import ProductPerformanceMethod from './ProductPerformanceMethod';
import ProductPerformanceTable from './ProductPerformanceTable';
import ProductPerformanceTrend from './ProductPerformanceTrend';
import ProductPerformanceWeekly from './ProductPerformanceWeekly';
import {
  buildMarginOption,
  buildMarginValueOption,
  buildProportionOption,
  buildSalesOption,
  buildWeeklyOption,
} from './charts';
import { comparisonCsv, comparisonData } from './comparisons';
import { type MetricColumn, columnsFor, metricColumns, metricCsvValue, metricText } from './metrics';
import {
  type CardProblem,
  type CardResult,
  type FilterKey,
  type MetricKey,
  PRODUCT_PERFORMANCE_PAGE_ID,
  type ProductFilters,
  type ProductGroup,
  REPORT_VIEWS,
  type ReportView,
  TAB_CARDS,
  defaultFilters,
  filterOptionsFor,
  filtersError,
  groupsOf,
  isProblem,
  lensFilters,
  lowMarginGroups,
  marginShare,
  parseFilters,
  periodLabel,
  pointsOf,
  readCard,
  summaryOf,
  writeFilters,
} from './model';
import { editFromBridge, productPerformancePageView } from './pageAdapter';

const LENS_ID = nativeLensId(PRODUCT_PERFORMANCE_PAGE_ID);
const COMPARISONS = [
  { card: 'c_destination', key: 'destination_country', label: 'Destination country', title: 'Destination comparisons' },
  { card: 'c_product', key: 'product', label: 'Product', title: 'Product comparisons' },
  { card: 'c_currency', key: 'order_currency', label: 'Order currency', title: 'Currency comparisons' },
] as const;
const SOURCE = 'lux-analytics-prod.reporting.sales';
const LOW_MARGIN_CARD = 'l_groups_window';
const WEEKLY_CARD = 'w_overall_window';
// The card whose resolved window labels each tab.
const PERIOD_CARD: Record<ReportView, string> = {
  product: 'p_product',
  margin: 'm_product',
  'low-margin': LOW_MARGIN_CARD,
  weekly: WEEKLY_CARD,
  comparisons: 'c_product',
};
// m_deal_vendor's limit in the spec: the Lens row cap. A 30-day window has about 9,000 pairs.
const DEAL_VENDOR_LIMIT = 10000;

const measures: Record<string, Array<MetricKey>> = {
  destination: ['sales', 'margin', 'packages', 'nights', 'adr', 'leadTime', 'los'],
  product: ['sales', 'margin', 'marginRate', 'packages', 'nights', 'adr', 'leadTime', 'los'],
  deal: [
    'sales',
    'margin',
    'marginRate',
    'packages',
    'orders',
    'nights',
    'adr',
    'leadTime',
    'los',
    'adults',
    'children',
    'infants',
  ],
  brand: ['sales', 'margin', 'packages', 'nights', 'leadTime', 'los'],
  margin: ['sales', 'margin', 'marginRate', 'packages', 'nights', 'leadTime'],
  marginProduct: ['sales', 'margin', 'marginRate', 'orders', 'nights', 'leadTime'],
  low: ['sales', 'margin', 'marginRate', 'nights'],
};

type TableSection = {
  key: string;
  title: string;
  dimensionLabel: string;
  card: string;
  keys: Array<string>;
  drill: Array<FilterKey>;
  measures: Array<MetricKey>;
  orders?: string;
  wide?: boolean;
  note?: string;
};

const productTables: Array<TableSection> = [
  {
    key: 'destination',
    title: 'Destination breakdown',
    dimensionLabel: 'Destination country',
    card: 'p_destination',
    keys: ['destination_country'],
    drill: ['destination_country'],
    measures: measures.destination,
  },
  {
    key: 'product',
    title: 'Product breakdown',
    dimensionLabel: 'Product',
    card: 'p_product',
    keys: ['product'],
    drill: ['product'],
    measures: measures.product,
  },
  {
    key: 'deal',
    title: 'Deal breakdown',
    dimensionLabel: 'Opportunity',
    card: 'p_deal',
    keys: ['opportunity'],
    drill: ['opportunity'],
    measures: measures.deal,
    orders: 'p_deal_orders',
    wide: true,
  },
  {
    key: 'currency',
    title: 'Currency (LE market) breakdown',
    dimensionLabel: 'Order currency',
    card: 'p_currency',
    keys: ['order_currency'],
    drill: ['order_currency'],
    measures: measures.destination,
  },
  {
    key: 'brand',
    title: 'Brand breakdown',
    dimensionLabel: 'Brand',
    card: 'p_brand',
    keys: ['brand'],
    drill: ['brand'],
    measures: measures.brand,
  },
];
const marginTables: Array<TableSection> = [
  {
    key: 'destination',
    title: 'Destination breakdown',
    dimensionLabel: 'Destination country',
    card: 'm_destination',
    keys: ['destination_country'],
    drill: ['destination_country'],
    measures: measures.margin,
  },
  {
    key: 'dealVendor',
    title: 'Deal and vendor breakdown',
    dimensionLabel: 'Opportunity · vendor',
    card: 'm_deal_vendor',
    keys: ['opportunity', 'vendor'],
    drill: ['opportunity', 'vendor'],
    measures: measures.margin,
    wide: true,
    note: `Largest ${DEAL_VENDOR_LIMIT.toLocaleString('en-AU')} opportunity and vendor pairs by sales. Use the Opportunity or Vendor filter to see any other pair.`,
  },
  {
    key: 'currency',
    title: 'Currency (LE market) breakdown',
    dimensionLabel: 'Order currency',
    card: 'm_currency',
    keys: ['order_currency'],
    drill: ['order_currency'],
    measures: measures.margin,
  },
  {
    key: 'product',
    title: 'Product breakdown',
    dimensionLabel: 'Product',
    card: 'm_product',
    keys: ['product'],
    drill: ['product'],
    measures: measures.marginProduct,
    orders: 'm_product_orders',
  },
];

function csvFor(
  title: string,
  range: string,
  dimensionLabel: string,
  rows: Array<ProductGroup>,
  columns: Array<MetricColumn>,
) {
  const cell = (value: unknown) => `"${String(value ?? 'Unknown').replace(/"/g, '""')}"`;
  const lines = [
    ['Report', `Product Performance · ${title}`],
    ['Source', SOURCE],
    ['Period', range],
    [dimensionLabel, ...columns.map((column) => column.label)],
    ...rows.map((row) => [
      row.label,
      ...columns.map((column) => metricCsvValue(row.metrics[column.key], column) ?? 'Unknown'),
    ]),
  ];
  return '﻿' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function ProductPerformanceContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const dark = theme.palette.mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const [methodOpen, setMethodOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<ProductFilters>) => {
    const next = { ...filters, ...change };
    navigate({
      pathname: location.pathname,
      search: writeFilters(next, new URLSearchParams(location.search)).toString(),
      hash: location.hash,
    });
  };
  const error = filtersError(filters);
  // Filter values come back with each load; the last full list stays in place while the next one loads.
  const lastOptions = useRef<Record<string, Array<string>>>({});
  const request = useMemo(() => lensFilters(filters, lastOptions.current), [filters]);
  // Only the open tab's cards run; Low Margin and Weekly Trends send their own window.
  const query = useGetReportingLeLensData(
    error ? undefined : LENS_ID,
    request,
    true,
    undefined,
    undefined,
    undefined,
    TAB_CARDS[filters.reportView],
  );
  const cards = query.data?.cards ?? [];
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = filterOptionsFor(filters.reportView, lastOptions.current);
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const range = periodLabel(
    cards.find((card) => card.cardId === PERIOD_CARD[filters.reportView]),
    `${filters.from} to ${filters.to}`,
  );
  const card = (id: string) => readCard(cards, id);

  const tables = filters.reportView === 'product' ? productTables : filters.reportView === 'margin' ? marginTables : [];
  const tableData = tables.map((section) => {
    const result = card(section.card);
    return {
      section,
      result,
      rows: isProblem(result) ? [] : groupsOf(result, section.keys, section.orders ? card(section.orders) : undefined),
    };
  });
  // Only the Product and Margin tabs have headline totals. The other tabs request only their own
  // cards, so they report no summary rather than totals from another tab's filters.
  const summarySource =
    filters.reportView === 'margin' ? card('m_product') : filters.reportView === 'product' ? card('p_product') : null;
  const summary = !summarySource || isProblem(summarySource) ? null : summaryOf(summarySource);
  const summaryEmpty = Boolean(summarySource && !isProblem(summarySource) && summarySource.rows.length === 0);
  const lowResult = card(LOW_MARGIN_CARD);
  const lowRows = isProblem(lowResult) ? [] : lowMarginGroups(lowResult, filters.minMargin, filters.maxMargin);

  const drill = (keys: Array<FilterKey>, row: ProductGroup) => {
    const selections = { ...filters.selections };
    keys.forEach((key, index) => {
      selections[key] = [row.dimensions[Object.keys(row.dimensions)[index]] ?? 'Unknown'];
    });
    update({ selections });
  };
  const selectProduct = (product: string) => update({ selections: { ...filters.selections, product: [product] } });

  const comparisons = COMPARISONS.map((panel) => {
    const result = card(panel.card);
    return { panel, result, data: isProblem(result) ? null : comparisonData(result, panel.key) };
  });
  const asSection = (title: string, rows: Array<ProductGroup>, truncated: boolean) => ({
    title,
    rows: rows.map((row) => ({ label: row.label, values: row.metrics })),
    truncated,
  });
  const pageView = productPerformancePageView({
    ready,
    filters,
    options,
    period: range,
    summary,
    sections:
      filters.reportView === 'low-margin'
        ? [asSection('Low-margin transactions', lowRows, !isProblem(lowResult) && lowResult.truncated)]
        : filters.reportView === 'comparisons'
          ? comparisons.map(({ panel, result, data }) => ({
              title: panel.title,
              rows: (data?.rows ?? []).map((row) => ({
                label: row.label,
                values: Object.fromEntries(
                  (data?.months ?? []).flatMap((month) => [
                    [`${month} sales`, row.sales[month] ?? null],
                    [`${month} change`, row.changes[month] ?? null],
                  ]),
                ),
              })),
              truncated: !isProblem(result) && result.truncated,
            }))
          : tableData.map(({ section, result, rows }) =>
              asSection(section.title, rows, !isProblem(result) && result.truncated),
            ),
  });
  usePageBridge({
    pageId: PRODUCT_PERFORMANCE_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters));
    },
  });

  const problem = (result: CardResult | CardProblem) =>
    isProblem(result) ? (
      <Alert severity={query.isFetching ? 'info' : 'error'}>{query.isFetching ? 'Loading…' : result.problem}</Alert>
    ) : null;
  const rowLimit = (result: CardResult | CardProblem) =>
    !isProblem(result) && result.truncated ? (
      <Alert severity="warning">
        This section reached its row limit, so some rows are not shown. Narrow the filters or dates to see every row.
      </Alert>
    ) : null;
  const chart = (
    key: string,
    title: string,
    result: CardResult | CardProblem,
    option: (result: CardResult) => EChartsOption,
    height = 340,
    onSelect?: (product: string) => void,
  ) =>
    isProblem(result) ? (
      <Box key={key}>{problem(result)}</Box>
    ) : (
      <Stack key={key} gap={1}>
        {rowLimit(result)}
        <ProductPerformanceTrend
          title={title}
          option={option(result)}
          source={SOURCE}
          range={periodLabel(result.card, range)}
          height={height}
          onSelect={onSelect ? (selection) => onSelect(selection.product) : undefined}
        />
      </Stack>
    );
  const table = ({ section, result, rows }: (typeof tableData)[number]) => {
    const columns = columnsFor(section.measures);
    return (
      <Stack key={section.key} gap={1} sx={{ minWidth: 0, gridColumn: section.wide ? '1 / -1' : undefined }}>
        {problem(result)}
        {rowLimit(result)}
        {!isProblem(result) && (
          <>
            {section.note && rows.length >= DEAL_VENDOR_LIMIT && <Alert severity="info">{section.note}</Alert>}
            <ProductPerformanceTable
              title={section.title}
              dimensionLabel={section.dimensionLabel}
              rows={rows}
              columns={columns}
              source={SOURCE}
              range={range}
              unavailable={section.orders ? [] : ['orders']}
              onDrill={(row) => drill(section.drill, row)}
              onCsv={() =>
                download(
                  `product-performance-${filters.reportView}-${section.key}-${filters.from}-${filters.to}.csv`,
                  csvFor(
                    section.title,
                    range,
                    section.dimensionLabel,
                    rows,
                    metricColumns.filter((column) => section.orders || column.key !== 'orders'),
                  ),
                )
              }
            />
          </>
        )}
      </Stack>
    );
  };
  const kpis = summary && (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
        gap: 1.5,
      }}
    >
      {(
        [
          { label: 'Sales AUD', key: 'sales' },
          { label: 'Adjusted margin AUD', key: 'margin' },
          { label: 'Packages', key: 'packages' },
          { label: 'Nights', key: 'nights' },
        ] as const
      ).map((metric) => (
        <Paper key={metric.key} variant="outlined" sx={{ p: 1.5, minWidth: 0, bgcolor: tokens.surface }}>
          <Typography variant="caption" color="text.secondary">
            {metric.label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: theme.typography.body1.fontFamily,
              fontSize: { xs: 20, sm: 23 },
              fontVariantNumeric: 'tabular-nums',
              overflowWrap: 'anywhere',
              color: tokens.accent,
            }}
          >
            {/* These four are sums, so a scope with no matching rows is 0 rather than no value. */}
            {metricText(summary[metric.key] ?? (summaryEmpty ? 0 : null))}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
  const marginDaily = card('m_daily');
  const marginPoints = isProblem(marginDaily) ? [] : pointsOf(marginDaily, 'product', (metrics) => metrics.margin);
  const comparisonResult = card('c_product');
  const comparisonWindow = isProblem(comparisonResult)
    ? 'This month and the 3 before it'
    : `${periodLabel(comparisonResult.card, 'This month and the 3 before it')} (this month to yesterday and the 3 months before it)`;
  const weeklyResult = card(WEEKLY_CARD);
  const productScope = filters.selections.product.length ? filters.selections.product.join(', ') : 'All products';

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              Product Performance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Original report measures and breakdowns · Sydney business dates · AUD
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <Button onClick={() => setMethodOpen(true)} size="small">
              Method and sources
            </Button>
            <PageChatButton />
          </Stack>
        </Stack>
        <Tabs
          value={filters.reportView}
          onChange={(_, reportView: ReportView) => update(defaultFilters(reportView))}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Product Performance report views"
        >
          {REPORT_VIEWS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            {SOURCE} · rebuilt daily · {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        <ProductPerformanceFilters
          filters={filters}
          options={options}
          onChange={update}
          advanced={advanced}
          onAdvancedChange={() => setAdvanced((value) => !value)}
          fixedWindow={filters.reportView === 'comparisons' ? comparisonWindow : undefined}
        />
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'Product Performance is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
              : failure.status === 403
                ? 'You do not have access to Product Performance. Ask a reporting admin to add you to its Library folder.'
                : failure.message || 'Product Performance could not load.'}
          </Alert>
        )}
        {!error && !failure && query.isPending && (
          <Stack gap={2}>
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={340} />
          </Stack>
        )}
        {!error && query.data && (
          <>
            {filters.reportView === 'margin' && (
              <Alert severity="info">
                Margin Performance starts on the report’s saved products: Limited Time Lux Exclusive, Limited Time Lux
                Partner, Lux Partner Property and Lux Premium Collection. Change Product to widen or narrow it. It
                always uses the report’s 25 margin currencies.
              </Alert>
            )}
            {(filters.reportView === 'product' || filters.reportView === 'margin') && kpis}
            {filters.reportView === 'product' &&
              chart(
                'sales',
                'Sales trend by product',
                card('p_daily'),
                (result) =>
                  buildSalesOption(
                    pointsOf(result, 'product', (metrics) => metrics.sales),
                    dark,
                  ),
                340,
                selectProduct,
              )}
            {filters.reportView === 'margin' &&
              chart(
                'margin',
                'Total margin % by product',
                marginDaily,
                (result) =>
                  buildMarginOption(
                    pointsOf(result, 'product', (metrics) => metrics.totalMarginRate),
                    dark,
                  ),
                340,
                selectProduct,
              )}
            {filters.reportView === 'low-margin' && (
              <Stack gap={1}>
                {problem(lowResult)}
                {rowLimit(lowResult)}
                {!isProblem(lowResult) && (
                  <>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6">
                        {lowRows.length.toLocaleString('en-AU')} qualifying transaction groups
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {productScope} · Adjusted margin {metricText(filters.minMargin, 'percent')} to{' '}
                        {metricText(filters.maxMargin, 'percent')} · Grouped by vendor, booking and transaction time.
                      </Typography>
                    </Paper>
                    <ProductPerformanceTable
                      title="Low-margin transactions"
                      dimensionLabel="Vendor"
                      rows={lowRows}
                      columns={columnsFor(measures.low)}
                      transactionDetail
                      source={SOURCE}
                      range={periodLabel(lowResult.card, range)}
                      unavailable={['orders']}
                      onCsv={() =>
                        download(
                          `product-performance-low-margin-${filters.minMargin}-${filters.maxMargin}.csv`,
                          csvFor(
                            'Low-margin transactions',
                            periodLabel(lowResult.card, range),
                            'Vendor · booking · transaction time',
                            lowRows,
                            columnsFor(measures.low),
                          ),
                        )
                      }
                    />
                  </>
                )}
              </Stack>
            )}
            {tableData.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  minWidth: 0,
                }}
              >
                {tableData.map(table)}
              </Box>
            )}
            {filters.reportView === 'margin' && (
              <>
                {chart(
                  'margin-value',
                  'Adjusted margin AUD by product',
                  marginDaily,
                  () => buildMarginValueOption(marginPoints, dark),
                  340,
                  selectProduct,
                )}
                {chart(
                  'proportion',
                  'Product share of adjusted margin',
                  marginDaily,
                  () => buildProportionOption(marginShare(marginPoints), dark),
                  340,
                  selectProduct,
                )}
              </>
            )}
            {filters.reportView === 'weekly' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Weekly views start on the report’s saved cruise, flight, hotel and tour products and never include the
                  current week. Select a series to filter; clear its chip to return.
                </Typography>
                {chart(
                  'weekly',
                  'Weekly sales',
                  weeklyResult,
                  (result) =>
                    buildWeeklyOption(
                      pointsOf(result, null, (metrics) => metrics.sales, true),
                      dark,
                    ),
                  360,
                )}
                {(
                  [
                    { card: 'w_destination_window', key: 'destination_country', title: 'Weekly destination trends' },
                    { card: 'w_product_window', key: 'product', title: 'Weekly product trends' },
                    { card: 'w_customer_window', key: 'customer_country', title: 'Weekly customer market trends' },
                  ] as const
                ).map((panel) => {
                  const result = card(panel.card);
                  return isProblem(result) ? (
                    <Box key={panel.card}>{problem(result)}</Box>
                  ) : (
                    <Stack key={panel.card} gap={1}>
                      {rowLimit(result)}
                      <ProductPerformanceWeekly
                        title={panel.title}
                        points={pointsOf(result, panel.key, (metrics) => metrics.sales, true)}
                        source={SOURCE}
                        range={periodLabel(result.card, range)}
                        onSelect={(value) => update({ selections: { ...filters.selections, [panel.key]: [value] } })}
                      />
                    </Stack>
                  );
                })}
              </>
            )}
            {filters.reportView === 'comparisons' &&
              comparisons.map(({ panel, result, data }) =>
                isProblem(result) || !data ? (
                  <Box key={panel.card}>{problem(result)}</Box>
                ) : (
                  <Stack key={panel.card} gap={1}>
                    {rowLimit(result)}
                    <ProductPerformanceComparisons
                      title={panel.title}
                      dimension={panel.key}
                      dimensionLabel={panel.label}
                      data={data}
                      source={SOURCE}
                      range={periodLabel(result.card, comparisonWindow)}
                      onSelect={(value) => update({ selections: { ...filters.selections, [panel.key]: [value] } })}
                      onCsv={() =>
                        download(
                          `product-performance-${panel.card}.csv`,
                          comparisonCsv(data, panel.title, panel.label, periodLabel(result.card, comparisonWindow)),
                        )
                      }
                    />
                  </Stack>
                ),
              )}
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
              Source: {SOURCE} · {range}. Sums skip missing values; Unknown means no computable value.
            </Typography>
          </>
        )}
      </Stack>
      <ProductPerformanceMethod open={methodOpen} onClose={() => setMethodOpen(false)} />
    </Container>
  );
}

export default function ProductPerformancePage() {
  return (
    <MUIProvider>
      <PageChatLayout>
        <ProductPerformanceContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
